
import React from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, Check, Clock, Bookmark, 
  ThumbsUp, ThumbsDown, MessageCircle,
  Shield, Lock, AlertTriangle, KeyRound, 
  Fingerprint, QrCode, Smartphone, MailOpen
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface AuthenticationMethodsContentProps {
  activePage: string;
  feedbackSubmitted: Record<string, boolean>;
  bookmarkedArticles: string[];
  submitFeedback: (articleId: string, isHelpful: boolean) => void;
  toggleBookmark: (articleId: string) => void;
}

export const AuthenticationMethodsContent: React.FC<AuthenticationMethodsContentProps> = ({
  activePage,
  feedbackSubmitted,
  bookmarkedArticles,
  submitFeedback,
  toggleBookmark,
}) => {
  if (activePage === "authentication-methods") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Authentication Methods</h1>
          <p className="text-gray-500 mt-2">
            Learn about the available authentication methods and how to configure them for your organization.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: September 5, 2023</span>
            <Badge variant="outline" className="ml-2">Security</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="authentication-overview">Authentication Overview</h2>
          <p>
            DGK Platform offers multiple authentication methods to secure your account and meet your organization's 
            security requirements. You can configure one or more methods based on your security policies and user preferences.
          </p>

          <Alert className="my-6 bg-blue-50">
            <Shield className="h-5 w-5 text-blue-500" />
            <AlertTitle className="text-blue-700">Multi-factor Authentication Recommended</AlertTitle>
            <AlertDescription className="text-blue-600">
              For the highest level of security, we recommend implementing at least two authentication factors for all users.
              Enterprise accounts require multi-factor authentication by default.
            </AlertDescription>
          </Alert>

          <Tabs defaultValue="passwordless" className="my-6">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
              <TabsTrigger value="passwordless">Passwordless</TabsTrigger>
              <TabsTrigger value="mfa">Multi-Factor</TabsTrigger>
              <TabsTrigger value="sso">SSO</TabsTrigger>
              <TabsTrigger value="api">API Authentication</TabsTrigger>
            </TabsList>
            
            <TabsContent value="passwordless" className="pt-4">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Passwordless Authentication</h3>
                <p>
                  Passwordless authentication methods eliminate the need for traditional passwords, 
                  enhancing security while improving the user experience.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-start">
                        <div className="mr-3 p-2 bg-blue-50 rounded-full">
                          <MailOpen className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Magic Link</h4>
                          <p className="text-sm text-gray-600">
                            Users receive a secure link via email that authenticates them automatically when clicked.
                          </p>
                          <div className="mt-3 text-sm">
                            <span className="text-gray-700 font-medium">Setup: </span>
                            <span className="text-gray-600">Easy</span>
                          </div>
                          <div className="mt-1 text-sm">
                            <span className="text-gray-700 font-medium">User Experience: </span>
                            <span className="text-gray-600">Excellent</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-start">
                        <div className="mr-3 p-2 bg-blue-50 rounded-full">
                          <Fingerprint className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Biometric Authentication</h4>
                          <p className="text-sm text-gray-600">
                            Leverages device biometrics like fingerprint, face ID, or Windows Hello for secure login.
                          </p>
                          <div className="mt-3 text-sm">
                            <span className="text-gray-700 font-medium">Setup: </span>
                            <span className="text-gray-600">Medium</span>
                          </div>
                          <div className="mt-1 text-sm">
                            <span className="text-gray-700 font-medium">User Experience: </span>
                            <span className="text-gray-600">Excellent</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-6">
                  <h4 className="font-medium mb-2">Implementation Considerations</h4>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-green-500 mt-1 mr-2 flex-shrink-0" />
                      <span>Magic links expire after 10 minutes for security</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-green-500 mt-1 mr-2 flex-shrink-0" />
                      <span>Biometric data never leaves the user's device</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-green-500 mt-1 mr-2 flex-shrink-0" />
                      <span>Provide fallback methods for users who can't use passwordless options</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-green-500 mt-1 mr-2 flex-shrink-0" />
                      <span>Email domains can be restricted for enterprise accounts</span>
                    </li>
                  </ul>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="mfa" className="pt-4">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Multi-Factor Authentication</h3>
                <p>
                  Multi-factor authentication provides an additional layer of security by requiring users to 
                  verify their identity using two or more different factors.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-start">
                        <div className="mr-3 p-2 bg-indigo-50 rounded-full">
                          <Smartphone className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Authenticator Apps</h4>
                          <p className="text-sm text-gray-600">
                            Time-based one-time passwords generated by apps like Google Authenticator or Authy.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-start">
                        <div className="mr-3 p-2 bg-indigo-50 rounded-full">
                          <QrCode className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">SMS/Text Message</h4>
                          <p className="text-sm text-gray-600">
                            One-time codes sent via SMS to the user's registered mobile number.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card>
                    <CardContent className="pt-6">
                      <div className="flex items-start">
                        <div className="mr-3 p-2 bg-indigo-50 rounded-full">
                          <KeyRound className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div>
                          <h4 className="font-medium mb-2">Security Keys</h4>
                          <p className="text-sm text-gray-600">
                            Physical security keys (FIDO2/WebAuthn) that provide hardware-based authentication.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <Alert className="my-4 bg-amber-50 border-amber-200">
                  <AlertTriangle className="h-5 w-5 text-amber-600" />
                  <AlertTitle className="text-amber-800">SMS Authentication Considerations</AlertTitle>
                  <AlertDescription className="text-amber-700">
                    While SMS authentication is convenient, it's vulnerable to SIM swapping attacks. 
                    We recommend using authenticator apps or security keys for higher security.
                  </AlertDescription>
                </Alert>
                
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-4">
                  <h4 className="font-medium mb-2">MFA Recovery Options</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    It's important to configure recovery options in case users lose access to their MFA devices:
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-green-500 mt-1 mr-2 flex-shrink-0" />
                      <span>Backup codes (generate and store securely)</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-green-500 mt-1 mr-2 flex-shrink-0" />
                      <span>Secondary email recovery</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-4 w-4 text-green-500 mt-1 mr-2 flex-shrink-0" />
                      <span>Admin-assisted recovery (for enterprise accounts)</span>
                    </li>
                  </ul>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="sso" className="pt-4">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Single Sign-On (SSO)</h3>
                <p>
                  Enterprise organizations can implement SSO to streamline authentication across multiple applications
                  while maintaining centralized security control.
                </p>
                
                <Accordion type="single" collapsible className="w-full mt-4">
                  <AccordionItem value="saml">
                    <AccordionTrigger className="font-medium">
                      SAML 2.0 Integration
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <p className="text-sm text-gray-600">
                          Security Assertion Markup Language (SAML) 2.0 allows secure exchange of authentication and 
                          authorization data between the DGK Platform and your identity provider.
                        </p>
                        <div className="bg-gray-50 p-3 rounded-md">
                          <h4 className="font-medium text-sm mb-2">Supported Identity Providers:</h4>
                          <ul className="text-sm space-y-1">
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-green-500 mr-2" />
                              <span>Okta</span>
                            </li>
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-green-500 mr-2" />
                              <span>Azure AD</span>
                            </li>
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-green-500 mr-2" />
                              <span>Google Workspace</span>
                            </li>
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-green-500 mr-2" />
                              <span>OneLogin</span>
                            </li>
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-green-500 mr-2" />
                              <span>PingIdentity</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  
                  <AccordionItem value="oauth">
                    <AccordionTrigger className="font-medium">
                      OAuth 2.0 / OpenID Connect
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <p className="text-sm text-gray-600">
                          OAuth 2.0 and OpenID Connect provide standardized authentication flows for secure delegation of access.
                        </p>
                        <div className="bg-gray-50 p-3 rounded-md">
                          <h4 className="font-medium text-sm mb-2">Supported Providers:</h4>
                          <ul className="text-sm space-y-1">
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-green-500 mr-2" />
                              <span>Google</span>
                            </li>
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-green-500 mr-2" />
                              <span>Microsoft</span>
                            </li>
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-green-500 mr-2" />
                              <span>Apple</span>
                            </li>
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-green-500 mr-2" />
                              <span>GitHub</span>
                            </li>
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-green-500 mr-2" />
                              <span>LinkedIn</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                  
                  <AccordionItem value="just-in-time">
                    <AccordionTrigger className="font-medium">
                      Just-in-Time Provisioning
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-3 pt-2">
                        <p className="text-sm text-gray-600">
                          Automatically create user accounts in the DGK Platform when users authenticate through your 
                          identity provider for the first time.
                        </p>
                        <div className="bg-gray-50 p-3 rounded-md">
                          <h4 className="font-medium text-sm mb-2">Key Features:</h4>
                          <ul className="text-sm space-y-1">
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-green-500 mr-2" />
                              <span>Automatic role assignment based on IdP attributes</span>
                            </li>
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-green-500 mr-2" />
                              <span>Group mapping from IdP to platform permissions</span>
                            </li>
                            <li className="flex items-center">
                              <Check className="h-4 w-4 text-green-500 mr-2" />
                              <span>Profile data synchronization</span>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
                
                <div className="mt-6">
                  <p className="text-sm text-gray-700 mb-4">
                    SSO implementation requires our Enterprise plan. Contact your account representative for 
                    detailed configuration guides specific to your identity provider.
                  </p>
                  <Button variant="outline" className="flex items-center">
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Request SSO Implementation
                  </Button>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="api" className="pt-4">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">API Authentication</h3>
                <p>
                  For machine-to-machine communication and integrating with our API, we provide multiple authentication methods:
                </p>
                
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mt-4">
                  <h4 className="font-medium mb-3">API Keys</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Simple API keys for basic integrations and testing.
                  </p>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">Limited Access</Badge>
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Rate Limited</Badge>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h4 className="font-medium mb-3">OAuth 2.0 Client Credentials</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    Secure token-based authentication for production integrations.
                  </p>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Recommended</Badge>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Enterprise Ready</Badge>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <h4 className="font-medium mb-3">JWT Authentication</h4>
                  <p className="text-sm text-gray-600 mb-3">
                    JSON Web Tokens for stateless authentication with custom claims.
                  </p>
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Advanced</Badge>
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Enterprise Ready</Badge>
                  </div>
                </div>
                
                <Alert className="mt-6 bg-blue-50">
                  <Shield className="h-5 w-5 text-blue-500" />
                  <AlertTitle className="text-blue-700">API Security Best Practices</AlertTitle>
                  <AlertDescription className="text-blue-600">
                    <ul className="space-y-1 mt-2">
                      <li className="flex items-start">
                        <Check className="h-4 w-4 text-blue-500 mt-1 mr-2 flex-shrink-0" />
                        <span>Rotate API keys regularly</span>
                      </li>
                      <li className="flex items-start">
                        <Check className="h-4 w-4 text-blue-500 mt-1 mr-2 flex-shrink-0" />
                        <span>Use the principle of least privilege when assigning API permissions</span>
                      </li>
                      <li className="flex items-start">
                        <Check className="h-4 w-4 text-blue-500 mt-1 mr-2 flex-shrink-0" />
                        <span>Monitor API usage for unusual patterns</span>
                      </li>
                      <li className="flex items-start">
                        <Check className="h-4 w-4 text-blue-500 mt-1 mr-2 flex-shrink-0" />
                        <span>Store credentials securely in environment variables or secrets management</span>
                      </li>
                    </ul>
                  </AlertDescription>
                </Alert>
                
                <div className="mt-6">
                  <p className="text-sm mb-3">For detailed API authentication documentation, please refer to our API Reference:</p>
                  <Button variant="outline" className="flex items-center" asChild>
                    <Link to="/documentation?section=api-reference&page=authentication">
                      <ArrowRight className="h-4 w-4 mr-2" />
                      View API Authentication Docs
                    </Link>
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <h2 id="authentication-policies" className="mt-8">Authentication Policies</h2>
          <p>
            Configure authentication policies to enforce security standards across your organization:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 my-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <Lock className="h-5 w-5 text-purple-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium mb-2">Password Policies</h3>
                    <ul className="text-sm space-y-1 text-gray-600">
                      <li>Minimum length requirements</li>
                      <li>Complexity rules</li>
                      <li>Password history enforcement</li>
                      <li>Expiration settings</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Shield className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium mb-2">Security Controls</h3>
                    <ul className="text-sm space-y-1 text-gray-600">
                      <li>IP-based restrictions</li>
                      <li>Session timeout settings</li>
                      <li>Failed login attempt limits</li>
                      <li>Device trust levels</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["authentication-methods"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("authentication-methods", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("authentication-methods", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("authentication-methods") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("authentication-methods")}
            >
              {bookmarkedArticles.includes("authentication-methods") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
